/**
 * Cache command - Store test execution metadata after Playwright runs
 */

import type { Config } from '@/config';
import type { Command } from '@/types';
import { logger, setVerbose } from '@/utils/logger';
import { createProgressTracker } from '@/utils/progress';
import {
  PlaywrightShardDetector,
  type ShardInfo,
} from '@/core/shard-detection';
import {
  CacheExtractor,
  type CacheExtractionResult,
} from '@/core/cache-extractor';
import { CacheIdDetector, type CacheIdInfo } from '@/core/build-detector';
import { GitCollector, type GitMetadata } from '@/collectors/git';
import { CiCollector, type CiMetadata } from '@/collectors/ci';
import { CacheApiClient, type CachePayload } from '@/services/cache-api';

/**
 * CLI options specific to the cache command
 */
export interface CacheOptions {
  cacheId?: string; // Custom cache ID override
  workingDir?: string;
  token?: string;
  verbose?: boolean;
}

/**
 * Zod schema for cache options validation
 */
import { z } from 'zod';

export const CacheOptionsSchema = z.object({
  cacheId: z.string().optional(),
  workingDir: z.string().optional(),
  token: z.string().optional(),
  verbose: z.boolean().optional().default(false),
});

export type ValidatedCacheOptions = z.infer<typeof CacheOptionsSchema>;

/**
 * Cache command implementation
 */
export class CacheCommand implements Command {
  readonly name = 'cache';
  readonly description = 'Store test execution metadata after Playwright runs';

  constructor(private readonly config: Config) {}

  /**
   * Execute the cache command
   */
  async execute(options: ValidatedCacheOptions): Promise<void> {
    const progress = createProgressTracker();

    // Set verbose mode for logger
    setVerbose(options.verbose || false);

    try {
      progress.start('Caching test execution metadata...');

      if (options.verbose) {
        logger.verbose(
          `Cache configuration: workingDir=${options.workingDir || process.cwd()}, customCacheId=${options.cacheId || 'auto-detect'}`
        );
      }

      // Step 1: Detect working directory
      const workingDir = options.workingDir || process.cwd();
      progress.update(`Working directory: ${workingDir}`);

      // Step 2: Auto-detect Playwright configuration and shard info
      progress.update('Detecting Playwright configuration...');
      const shardInfo = await this.detectShardInfo(workingDir);

      if (options.verbose && shardInfo) {
        logger.verbose(
          `Detected shard configuration: ${shardInfo.shardIndex}/${shardInfo.shardTotal}`
        );
      }

      // Step 3: Extract test failure data using existing discovery and parsing
      progress.update('Discovering and extracting test data...');
      const failureData = await this.extractFailureData(workingDir);

      if (failureData.reportPaths.length === 0) {
        progress.warn(
          'No test reports found - tests may not have completed yet'
        );
        logger.info('Run this command after your Playwright tests complete');
        return; // Exit successfully (CI-friendly)
      }

      if (options.verbose) {
        logger.verbose(
          `Found ${failureData.failures.length} failed tests from ${failureData.reportPaths.length} reports`
        );
      }

      // Step 4: Collect build and CI metadata
      progress.update('Collecting build metadata...');
      const metadata = await this.collectBuildMetadata(options);

      // Step 5: Prepare cache payload
      const cachePayload = this.prepareCachePayload({
        metadata,
        shardInfo,
        failureData,
        timestamp: new Date().toISOString(),
      });

      if (options.verbose) {
        logger.verbose(`Cache payload summary:`);
        logger.kv('Cache ID', cachePayload.cacheId);
        logger.kv('Pipeline ID', cachePayload.pipelineId);
        logger.kv('Commit', cachePayload.commit);
        logger.kv('Sharded', cachePayload.isSharded ? 'Yes' : 'No');
        if (cachePayload.isSharded) {
          logger.kv(
            'Shard',
            `${cachePayload.shardIndex}/${cachePayload.shardTotal}`
          );
        }
        logger.kv('Failures', cachePayload.failures.length);
        logger.kv('Total Tests', cachePayload.summary.total);
        logger.kv('Branch', cachePayload.branch);
        logger.kv('Repository', cachePayload.repository);
      }

      // Step 7: Send to API
      progress.update('Sending cache data to TestDino API...');
      await this.sendCacheData(cachePayload);

      progress.succeed('Test execution metadata cached successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      // Special handling for cache conflicts (409) - this is not an error
      if (errorMessage.includes('already exists')) {
        progress.warn('Cache data already exists for this shard');
        logger.info('This shard has already been cached - skipping');
        logger.info('This warning will not affect your CI pipeline');
        return;
      }

      // CI-friendly error handling - warn but don't fail for other errors
      progress.warn(`Failed to cache test metadata: ${errorMessage}`);

      if (options.verbose) {
        logger.debug(`Full error details: ${error}`);
      }

      logger.info('This warning will not affect your CI pipeline');
      // Exit with success code (0) to avoid breaking CI
    }
  }

  /**
   * Auto-detect shard information from Playwright configuration
   */
  private async detectShardInfo(workingDir: string): Promise<ShardInfo | null> {
    return await PlaywrightShardDetector.detectShardInfo(workingDir);
  }

  /**
   * Discover and extract test failure data
   */
  private async extractFailureData(
    workingDir: string
  ): Promise<CacheExtractionResult> {
    const extractor = new CacheExtractor(workingDir);
    return await extractor.extractFailureData();
  }

  /**
   * Collect build and CI metadata using existing collectors
   */
  private async collectBuildMetadata(options: ValidatedCacheOptions): Promise<{
    cacheIdInfo: CacheIdInfo;
    gitMetadata: GitMetadata;
    ciMetadata: CiMetadata;
  }> {
    // Collect cache ID information from environment
    const cacheIdInfo = await CacheIdDetector.detectCacheId(options.cacheId);

    // Collect Git metadata
    const gitCollector = new GitCollector(options.workingDir || process.cwd());
    const gitMetadata = await gitCollector.getMetadata();

    // Collect CI metadata
    const ciMetadata = CiCollector.collect();

    return {
      cacheIdInfo,
      gitMetadata,
      ciMetadata,
    };
  }

  /**
   * Prepare cache payload for TestDino API based on PRD specification
   */
  private prepareCachePayload(data: {
    metadata: {
      cacheIdInfo: CacheIdInfo;
      gitMetadata: GitMetadata;
      ciMetadata: CiMetadata;
    };
    shardInfo: ShardInfo | null;
    failureData: CacheExtractionResult;
    timestamp: string;
  }): CachePayload {
    const { cacheIdInfo, ciMetadata } = data.metadata;
    const effectiveShardInfo =
      data.shardInfo || PlaywrightShardDetector.createDefaultShardInfo();

    return {
      // Cache identification (new format)
      cacheId: cacheIdInfo.cacheId,
      pipelineId: cacheIdInfo.pipelineId,
      commit: cacheIdInfo.commit,

      // Git metadata
      branch: cacheIdInfo.branch, // Use original branch from cacheIdInfo
      repository: cacheIdInfo.repository,

      // CI information
      ci: {
        provider: ciMetadata.provider || 'unknown',
        pipelineId: ciMetadata.pipeline?.id || 'unknown',
        buildNumber: ciMetadata.build?.number || 'unknown',
      },

      // Shard information (cleaner structure)
      isSharded: effectiveShardInfo.shardTotal > 1,
      shardIndex:
        effectiveShardInfo.shardTotal > 1
          ? effectiveShardInfo.shardIndex
          : null,
      shardTotal:
        effectiveShardInfo.shardTotal > 1
          ? effectiveShardInfo.shardTotal
          : null,

      // Test failure data (simplified)
      failures: data.failureData.failures.map(f => ({
        file: f.file,
        testTitle: f.testTitle,
        ...(f.project && { project: f.project }),
      })),

      // Test summary for this shard
      summary: data.failureData.summary,

      // Timestamp
      timestamp: data.timestamp,
    };
  }

  /**
   * Send cache data to TestDino API
   */
  private async sendCacheData(payload: CachePayload): Promise<void> {
    const apiClient = new CacheApiClient(this.config);

    // Check if cache endpoint is available (since APIs aren't implemented yet)
    const isEndpointAvailable = await apiClient.testCacheEndpoint();

    if (!isEndpointAvailable) {
      logger.info(
        'Cache API endpoint not available yet - using mock submission'
      );
      await apiClient.mockCacheSubmission(payload);
      return;
    }

    // Submit cache data to real API
    const result = await apiClient.submitCacheData(payload);

    if (result.success) {
      logger.success(`Cache data submitted for cache: ${result.cacheId}`);
      if (result.message) {
        logger.info(result.message);
      }
    } else {
      throw new Error(`Cache submission failed for cache: ${result.cacheId}`);
    }
  }
}
